import {
  BackBar,
  describeError,
  meterNoOrGenerated,
  nextSortOrder,
  PhotoField,
} from '@horof/shared';
import { Alert } from 'antd';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from 'urql';
import { MeterForm, type MeterFormValues } from './MeterForm';
import { CommitteeMetersDocument, CreateMeterDocument } from './meters-data';

/** `/meters/new?member=<id>` preselects the member (from their page). */
export function NewMeterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [{ fetching }, createMeter] = useMutation(CreateMeterDocument);
  const [{ data: meters }] = useQuery({ query: CommitteeMetersDocument });
  const [problem, setProblem] = useState<string | null>(null);
  // arqam doc 47: every new meter has a photo of its number and first reading.
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  async function create(values: MeterFormValues) {
    setProblem(null);
    if (!photoId) {
      setProblem('صورة العداد إلزامية');
      return;
    }
    const result = await createMeter(
      {
        meter: {
          memberId: values.memberId,
          meterNo: meterNoOrGenerated(values.meterNo ?? ''),
          meterAddress: values.meterAddress.trim(),
          // A new meter starts connected; changes go through its page.
          meterStatus: 'CONNECTED',
          photoId,
          // Last in the round (arqam's default, 0, would put it first).
          sortOrder: nextSortOrder(
            (meters?.meters?.nodes ?? []).map((m) => m?.sortOrder ?? null),
          ),
        },
      },
      { additionalTypenames: ['Meter'] },
    );
    const id = result.data?.createMeter?.meter?.id;
    if (result.error || !id) {
      setProblem(
        describeError(result.error, 'تعذّر إضافة العداد.', {
          'UNIQUE_VIOLATION:meters_meter_no_key': 'هذا الرقم مستعمل لعداد آخر.',
          METER_PHOTO_REQUIRED: 'صورة العداد إلزامية',
          PHOTO_WRONG_KIND: 'هذه ليست صورة عداد. التقط صورة العداد من جديد.',
          PHOTO_NOT_FOUND: 'تعذّر العثور على الصورة. التقطها من جديد.',
          MEMBER_LEFT: 'هذا العضو غادر الجمعية: لا يُضاف له عداد.',
        }),
      );
      return;
    }
    navigate(`/meters/${id}`, { replace: true });
  }

  const memberId = params.get('member') ?? undefined;
  return (
    <div className="page">
      <BackBar
        to={memberId ? `/members/${memberId}` : '/meters'}
        label={memberId ? 'العضو' : 'العدادات'}
      />
      <h1 className="member-title">عداد جديد</h1>
      {problem && <Alert type="error" showIcon message={problem} />}
      <MeterForm
        initialValues={{ memberId }}
        saving={fetching}
        submitLabel="إضافة العداد"
        onSubmit={create}
        submitDisabled={uploading}
        beforeSubmit={
          <PhotoField
            kind="METER_READING"
            label="صورة العداد (الرقم والقراءة الأولى)"
            required
            hint="صوّر العداد بحيث يظهر رقمه وقراءته الأولى بوضوح."
            value={photoId}
            onChange={setPhotoId}
            onBusyChange={setUploading}
          />
        }
      />
    </div>
  );
}
